import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, Pencil, CreditCard, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const cycleLabels = {
  monthly: "Mensuel",
  quarterly: "Trimestriel",
  yearly: "Annuel",
};

function PlanForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      slug: "",
      description: "",
      monthly_fee: 0,
      setup_fee: 0,
      billing_cycle: "monthly",
      status: "active",
    },
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      toast.error("Nom et slug requis");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="plan-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Nom</Label>
          <Input
            data-testid="plan-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#0B0F19] border-[#2A3441] text-white"
            placeholder="Ex: Performance SEO"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Slug</Label>
          <Input
            data-testid="plan-slug-input"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="bg-[#0B0F19] border-[#2A3441] text-white"
            placeholder="Ex: performance-seo"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#94A3B8]">Description</Label>
        <Input
          data-testid="plan-description-input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="bg-[#0B0F19] border-[#2A3441] text-white"
          placeholder="Description du plan..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Mensuel (EUR)</Label>
          <Input
            data-testid="plan-monthly-fee-input"
            type="number"
            step="0.01"
            min="0"
            value={form.monthly_fee}
            onChange={(e) => setForm({ ...form, monthly_fee: Number(e.target.value) })}
            className="bg-[#0B0F19] border-[#2A3441] text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Setup (EUR)</Label>
          <Input
            data-testid="plan-setup-fee-input"
            type="number"
            step="0.01"
            min="0"
            value={form.setup_fee}
            onChange={(e) => setForm({ ...form, setup_fee: Number(e.target.value) })}
            className="bg-[#0B0F19] border-[#2A3441] text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Cycle</Label>
          <select
            data-testid="plan-cycle-select"
            value={form.billing_cycle}
            onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
            className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10"
          >
            <option value="monthly">Mensuel</option>
            <option value="quarterly">Trimestriel</option>
            <option value="yearly">Annuel</option>
          </select>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-[#2A3441] text-[#94A3B8]"
          data-testid="plan-cancel-btn"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
          data-testid="plan-save-btn"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

export default function PricingPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/billing/plans`);
      setPlans(res.data);
    } catch {
      toast.error("Erreur chargement plans");
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async (payload) => {
    await axios.post(`${API}/billing/plans`, payload);
    toast.success("Plan cree");
    setCreating(false);
    await fetchPlans();
  };

  const updatePlan = async (payload) => {
    await axios.put(`${API}/billing/plans/${editing.id}`, payload);
    toast.success("Plan mis a jour");
    setEditing(null);
    await fetchPlans();
  };

  const archivePlan = async (planId) => {
    if (!window.confirm("Archiver ce plan ?")) return;
    try {
      await axios.delete(`${API}/billing/plans/${planId}`);
      toast.success("Plan archive");
      await fetchPlans();
    } catch {
      toast.error("Erreur archivage");
    }
  };

  const activePlans = plans.filter((p) => p.status === "active");
  const archivedPlans = plans.filter((p) => p.status === "archived");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pricing-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Plans & Tarifs</h1>
          <p className="text-[#94A3B8] mt-1">
            Gerez vos offres commerciales.{" "}
            <span className="text-xs text-[#64748b]">TVA non applicable, art. 293 B du CGI</span>
          </p>
        </div>
        {!creating && !editing && (
          <Button
            onClick={() => setCreating(true)}
            className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
            data-testid="new-plan-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau plan
          </Button>
        )}
      </div>

      {creating && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Nouveau plan</h3>
          <PlanForm onSave={createPlan} onCancel={() => setCreating(false)} />
        </div>
      )}

      {editing && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Modifier le plan</h3>
          <PlanForm initial={editing} onSave={updatePlan} onCancel={() => setEditing(null)} />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : activePlans.length === 0 && archivedPlans.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <CreditCard className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun plan</h3>
          <p className="text-[#94A3B8]">Creez votre premier plan tarifaire.</p>
        </div>
      ) : (
        <>
          {/* Active Plans */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="active-plans-grid">
            {activePlans.map((p) => (
              <div
                key={p.id}
                className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#0099CC]/30 transition-colors"
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white font-semibold text-lg">{p.name}</p>
                    <p className="text-xs text-[#64748b] mt-0.5 font-mono">{p.slug}</p>
                  </div>
                  <Badge className="bg-[#10B981]/20 text-[#10B981] border-0">Actif</Badge>
                </div>

                {p.description && (
                  <p className="text-sm text-[#94A3B8] mt-3">{p.description}</p>
                )}

                <div className="mt-4 pt-4 border-t border-[#2A3441]">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-3xl font-bold text-white">
                        {p.monthly_fee.toLocaleString("fr-FR")} <span className="text-base font-normal text-[#94A3B8]">EUR</span>
                      </p>
                      <p className="text-xs text-[#64748b] mt-1">
                        /{cycleLabels[p.billing_cycle]?.toLowerCase() || p.billing_cycle}
                        {p.setup_fee > 0 && ` + Setup ${p.setup_fee.toLocaleString("fr-FR")} EUR`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setCreating(false); setEditing(p); }}
                    className="flex-1 border-[#2A3441] text-[#94A3B8] hover:text-white"
                    data-testid={`edit-plan-${p.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => archivePlan(p.id)}
                    className="border-[#2A3441] text-[#EF4444] hover:bg-[#EF4444]/10"
                    data-testid={`archive-plan-${p.id}`}
                  >
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Archived Plans */}
          {archivedPlans.length > 0 && (
            <div data-testid="archived-plans-section">
              <h2 className="text-lg font-semibold text-[#64748b] mb-4">
                <Archive className="h-4 w-4 inline mr-2" />
                Plans archives ({archivedPlans.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedPlans.map((p) => (
                  <div
                    key={p.id}
                    className="bg-[#151B2B]/50 border border-[#2A3441]/50 rounded-xl p-5 opacity-60"
                    data-testid={`plan-card-archived-${p.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <p className="text-[#94A3B8] font-medium">{p.name}</p>
                      <Badge className="bg-[#64748b]/20 text-[#64748b] border-0">Archive</Badge>
                    </div>
                    <p className="text-sm text-[#64748b] mt-2">
                      {p.monthly_fee.toLocaleString("fr-FR")} EUR/{cycleLabels[p.billing_cycle]?.toLowerCase() || p.billing_cycle}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Legal mention */}
      <div className="text-center pt-4 border-t border-[#2A3441]/50">
        <p className="text-xs text-[#64748b]">
          TVA non applicable, art. 293 B du CGI
        </p>
      </div>
    </div>
  );
}
